import { useState } from "react";
import api from "@/lib/api";
import { Search, Loader2, AlertTriangle, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Diagnostics() {
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDiagnose = async (e) => {
    e.preventDefault();
    if (!number.trim()) return;
    
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/diagnostics/invoice/${encodeURIComponent(number.trim())}`);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mendiagnosa invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#0F172A]">Diagnostic Tool</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Alat pemecahan masalah (troubleshooting) untuk membandingkan data lokal Midware dengan data mentah dari API Accurate.
        </p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <form onSubmit={handleDiagnose} className="flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            <label className="block text-sm font-medium text-[#1E293B] mb-2">
              Nomor Faktur (Invoice Number)
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <input
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Misal: SI.2026.08.00060"
                className="w-full pl-9 pr-4 py-2 bg-white border border-[#E2E8F0] rounded-md text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={!number.trim() || loading}
            className="bg-[#0066FF] text-white hover:bg-[#0052CC] rounded-md px-6 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 h-[38px]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Diagnosa"}
          </button>
        </form>
      </div>

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden flex flex-col">
            <div className="bg-[#F8F9FA] px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
              <h3 className="font-semibold text-sm text-[#0F172A]">Data di Midware (Lokal)</h3>
            </div>
            <div className="p-4 flex-1 overflow-auto bg-[#F1F5F9]">
              <pre className="text-xs text-[#334155] whitespace-pre-wrap break-all">
                {JSON.stringify(result.local_data, null, 2)}
              </pre>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden flex flex-col">
            <div className="bg-[#FEF9C3] px-4 py-3 border-b border-[#FDE047] flex items-center gap-2">
              <h3 className="font-semibold text-sm text-[#854D0E]">Data di Accurate (Live API)</h3>
            </div>
            <div className="p-4 flex-1 overflow-auto bg-[#FEFCE8]">
              {result.accurate_raw_data?.error ? (
                <div className="text-red-600 text-sm flex items-start gap-2">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Error menarik data dari Accurate: {result.accurate_raw_data.error}</span>
                </div>
              ) : (
                <pre className="text-xs text-[#854D0E] whitespace-pre-wrap break-all">
                  {JSON.stringify(result.accurate_raw_data, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
